import { z } from 'zod';

/**
 * Wire schemas for business API responses
 * These match the backend response format (snake_case)
 *
 * IMPORTANT: Backend stores:
 * - Timestamps as ISO string DATETIME, not Unix numbers
 * - Optional fields can be null, not just undefined
 * - Tags are returned as parsed JSON array
 * - Hours are returned as JSON object
 */

/**
 * Business wire schema (matches backend response format)
 * Based on BusinessReadSchema from Gateway
 */
export const businessWireSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  status: z.enum(['active', 'inactive', 'pending']).default('active'),
  owner_id: z.string().nullable().optional(),
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
  address: z.string().nullable().optional(), // D1 legacy field
  street: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
  postal_code: z.string().nullable().optional(),
  timezone: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  email: z.string().email().nullable().optional(),
  website: z.string().url().nullable().optional(),
  category: z.string().nullable().optional(), // D1 legacy field
  cuisine_type: z.string().nullable().optional(),
  price_level: z.enum(['budget', 'moderate', 'expensive', 'luxury']).nullable().optional(),
  hours: z.record(z.string(), z.any()).nullable().optional(), // JSON from DB
  tags: z.array(z.string()).nullable().optional(), // D1 returns as parsed JSON array
  cover_photo_url: z.string().nullable().optional(),
  rating: z.number().min(0).max(5).nullable().optional(), // D1 field name
  total_ratings: z.number().int().min(0).nullish().transform(v => v ?? 0), // D1 field name - nullable/undefined becomes 0
  rating_distribution: z.object({
    not_for_me: z.number().int().min(0),
    okay: z.number().int().min(0),
    try_it: z.number().int().min(0),
    must_try: z.number().int().min(0),
  }).nullable().optional(),
  created_at: z.string(), // D1 DATETIME (ISO format)
  updated_at: z.string(), // D1 DATETIME (ISO format)
  // Source/provenance fields
  osm_id: z.string().nullable().optional(),
  osm_type: z.string().nullable().optional(),
  osm_full_id: z.string().nullable().optional(),
  geoapify_id: z.string().nullable().optional(),
  source_data_sources: z.string().nullable().optional(),
  source_last_updated: z.string().nullable().optional(),
  source_verified_by: z.string().nullable().optional(),
  source_notes: z.string().nullable().optional(),
});

// Use z.output to get the transformed output type (after transform is applied)
export type BusinessWire = z.output<typeof businessWireSchema>;

/**
 * Paginated response wrapper schema
 * Matches createPaginatedResponseSchema from Gateway
 */
export const paginatedBusinessResponseSchema = z.object({
  items: z.array(businessWireSchema),
  total: z.number().int().min(0),
  page: z.number().int().min(1),
  per_page: z.number().int().min(1).max(100),
  total_pages: z.number().int().min(0),
});

export type PaginatedBusinessResponseWire = z.infer<typeof paginatedBusinessResponseSchema>;
